import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, ClipboardList, Clock3, FileDown, Megaphone, ShieldCheck, UserPlus, Zap } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { HeroVisual } from "@/components/HeroVisual";
import { Layout } from "@/components/Layout";
import { ProductCard } from "@/components/ProductCard";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";

const icons = { "time-saving": Clock3, "customer-acquisition": UserPlus, marketing: Megaphone, operations: ClipboardList };
const benefits = [
  { title: "Reclaim repeated hours", text: "Use structured tools to turn recurring business work into clear, repeatable steps.", icon: Clock3 },
  { title: "Create a clearer customer path", text: "Organize lead capture, follow-up, and sales activity so opportunities are easier to see.", icon: UserPlus },
  { title: "Market with more consistency", text: "Plan campaigns and content with practical assets instead of starting from a blank page.", icon: Megaphone },
  { title: "Keep operations visible", text: "Bring daily priorities, owners, deadlines, and processes into one manageable view.", icon: ClipboardList },
];
const faqs = [
  ["What kinds of digital products will be sold here?", "The store is organized around practical tools for saving time, finding customers, improving marketing, and organizing operations. Individual products are added by the store owner before they are offered for sale."],
  ["How does delivery work?", "Each sellable product includes a digital file. After PayPal confirms payment, the site creates a secure delivery link for that order."],
  ["Can I use a product for my own business?", "Products are intended for use by the purchasing business. Reselling, redistributing, or publishing the files is not permitted unless a product explicitly says otherwise."],
  ["What if I have trouble accessing a file?", "Use the contact page and include your order details. Access problems and duplicate purchases are reviewed under the refund policy."],
  ["Are results guaranteed?", "No. These are practical tools, not promises of revenue, customer growth, or business outcomes. The value depends on how consistently they are applied."],
];

export function HomePage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data.slice(0, 3))).catch(() => setProducts([]));
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => setCategories([]));
  }, []);

  return (
    <Layout>
      <Seo title="Digital tools for small-business momentum" description="Practical digital products that help small businesses save time, get customers, improve marketing, and organize operations." />
      <section className="texture-noise relative overflow-hidden px-6 pb-20 pt-36 sm:pt-40 lg:min-h-screen lg:pb-24">
        <div className="absolute left-1/2 top-0 h-[34rem] w-[34rem] -translate-x-1/2 rounded-full bg-amber-500/10 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-[1.05fr_0.95fr]">
          <div>
            <Reveal className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#E6E2DA] bg-white px-4 py-2 font-mono text-xs uppercase tracking-[0.22em] text-amber-700 shadow-sm">
              <Zap className="h-3.5 w-3.5" /> Digital tools for real work
            </Reveal>
            <h1 className="text-balance font-heading text-4xl font-extrabold leading-[1.05] tracking-[-0.045em] sm:text-5xl lg:text-6xl">
              <MaskedLine delay={0.08}>Practical systems</MaskedLine>
              <MaskedLine delay={0.18}>for the business</MaskedLine>
              <MaskedLine delay={0.28} className="font-serif italic tracking-[-0.03em] text-[#3F5E4D]">you run every day.</MaskedLine>
            </h1>
            <Reveal delay={0.42} className="mt-7 max-w-2xl text-base leading-8 text-[#525866] sm:text-lg">
              Operandi Studio is a focused storefront for digital products that help small-business owners save time, organize customer growth, sharpen marketing, and keep daily operations moving.
            </Reveal>
            <Reveal delay={0.55} className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Button asChild size="lg" className="rounded-full bg-[#111315] px-7 text-white hover:bg-[#2a2e33]" data-testid="hero-shop-cta">
                <Link to="/shop">Explore the shop <ArrowRight className="h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full border-[#D2CCC0] bg-white/70 px-7" data-testid="hero-how-it-works-cta">
                <a href="#how-it-works">How delivery works</a>
              </Button>
            </Reveal>
            <Reveal delay={0.66} className="mt-10 grid max-w-xl grid-cols-1 gap-3 sm:grid-cols-3">
              {["PayPal checkout", "Secure delivery", "Clear policies"].map((item) => (
                <div key={item} className="flex items-center gap-2 rounded-2xl border border-[#E6E2DA] bg-white/70 px-4 py-3 text-sm font-semibold text-[#525866]">
                  <CheckCircle2 className="h-4 w-4 text-[#3F5E4D]" /> {item}
                </div>
              ))}
            </Reveal>
          </div>
          <HeroVisual />
        </div>
      </section>

      <section className="editorial-marquee border-y border-[#E6E2DA] bg-[#111315] py-5 text-[#FBF9F5]" aria-label="Store benefits marquee">
        <div className="marquee-track gap-10 font-serif text-2xl italic tracking-tight text-white/80">
          {[0, 1].map((copy) => (
            <div key={copy} className="flex shrink-0 items-center gap-10">
              {['Save time', 'Get customers', 'Improve marketing', 'Organize operations', 'Download practical tools'].map((item) => (
                <span key={`${copy}-${item}`} className="flex items-center gap-10"><span>{item}</span><span className="h-2 w-2 rounded-full bg-amber-500" /></span>
              ))}
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 py-24 lg:py-32">
        <div className="mx-auto max-w-7xl">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs uppercase tracking-[0.24em] text-amber-700">Featured categories</p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">Choose the business problem you want to make lighter.</h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-8 lg:grid-cols-12">
            {categories.map((category, index) => {
              const Icon = icons[category.slug] || ClipboardList;
              const spans = ["lg:col-span-7 md:col-span-5", "lg:col-span-5 md:col-span-3", "lg:col-span-5 md:col-span-3", "lg:col-span-7 md:col-span-5"];
              return (
                <Reveal key={category.slug} delay={index * 0.08} className={`${spans[index]} rounded-[2rem]`}>
                  <Link to={`/shop?category=${category.slug}`} data-testid={`category-card-${category.slug}`} className="group relative flex h-full min-h-[260px] flex-col justify-between overflow-hidden rounded-[2rem] border border-[#E6E2DA] bg-white p-8 shadow-[0_20px_70px_rgba(17,19,21,0.06)] transition-transform duration-300 hover:-translate-y-2">
                    <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-amber-500/10 transition-transform duration-500 group-hover:scale-150" />
                    <Icon className="h-9 w-9 text-[#3F5E4D]" />
                    <div className="relative mt-12">
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8C92A0]">{category.count} listed</p>
                      <h3 className="mt-2 font-heading text-2xl font-bold tracking-tight">{category.name}</h3>
                      <p className="mt-3 max-w-md text-sm leading-7 text-[#525866]">{category.description}</p>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-[#F4F0EA] px-6 py-24">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.8fr_1.2fr]">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.24em] text-amber-700">Why it helps</p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">Built around the four pressure points owners feel most.</h2>
            <p className="mt-5 text-base leading-8 text-[#525866]">The store stays practical: no inflated promises, no filler, and no mystery about what happens after purchase.</p>
          </Reveal>
          <div className="grid gap-4 sm:grid-cols-2">
            {benefits.map((benefit, index) => (
              <Reveal key={benefit.title} delay={index * 0.08} className="rounded-[1.75rem] border border-white bg-white/78 p-7 shadow-sm backdrop-blur">
                <benefit.icon className="h-6 w-6 text-amber-700" />
                <h3 className="mt-5 font-heading text-xl font-bold">{benefit.title}</h3>
                <p className="mt-3 text-sm leading-7 text-[#525866]">{benefit.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-24 lg:py-32">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.24em] text-amber-700">Product previews</p>
              <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">Fresh from the digital workbench.</h2>
            </Reveal>
            <Button asChild variant="outline" className="w-fit rounded-full" data-testid="preview-shop-link">
              <Link to="/shop">View all products</Link>
            </Button>
          </div>
          {products.length > 0 ? (
            <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {products.map((product, index) => <Reveal key={product.id} delay={index * 0.08}><ProductCard product={product} /></Reveal>)}
            </div>
          ) : (
            <Reveal className="mt-12 overflow-hidden rounded-[2.5rem] border border-[#E6E2DA] bg-[#111315] p-8 text-white md:p-12">
              <div className="grid items-center gap-10 lg:grid-cols-[1fr_0.8fr]">
                <div>
                  <FileDown className="h-10 w-10 text-amber-500" />
                  <h3 className="mt-6 font-heading text-3xl font-extrabold tracking-tight">The catalog is ready for its first release.</h3>
                  <p className="mt-4 max-w-2xl text-base leading-8 text-white/68">No invented products, prices, testimonials, or claims are shown here. Once the owner publishes a digital tool, it will appear with its own detail page, secure checkout, and delivery flow.</p>
                </div>
                <div className="rounded-[2rem] border border-white/10 bg-white/5 p-6">
                  <p className="font-mono text-xs uppercase tracking-[0.22em] text-amber-400">Store pipeline</p>
                  <div className="mt-5 space-y-4 text-sm text-white/72">
                    <p>01. Add the product and price</p>
                    <p>02. Attach the delivery file</p>
                    <p>03. Publish it to the shop</p>
                  </div>
                </div>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      <section id="how-it-works" className="bg-[#111315] px-6 py-24 text-white lg:py-32">
        <div className="mx-auto max-w-7xl">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs uppercase tracking-[0.24em] text-amber-500">How it works</p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">A simple path from product page to download.</h2>
          </Reveal>
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {[
              ["01", "Choose a tool", "Review the category, summary, price, and delivery details before checkout."],
              ["02", "Pay securely", "Checkout is handled through PayPal once the store credentials are connected."],
              ["03", "Download the file", "A completed payment creates a unique delivery link for the purchased product."],
            ].map(([step, title, text], index) => (
              <Reveal key={step} delay={index * 0.08} className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-8">
                <p className="font-mono text-xs uppercase tracking-[0.24em] text-amber-500">{step}</p>
                <h3 className="mt-8 font-heading text-2xl font-bold">{title}</h3>
                <p className="mt-4 text-sm leading-7 text-white/62">{text}</p>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12 grid gap-4 rounded-[2rem] border border-white/10 bg-white/[0.04] p-6 sm:grid-cols-3">
            {[
              [ShieldCheck, "Payment details stay with PayPal"],
              [FileDown, "Delivery is tied to a completed order"],
              [CheckCircle2, "Policies are visible before purchase"],
            ].map(([Icon, text]) => <div key={text} className="flex items-center gap-3 text-sm font-semibold text-white/78"><Icon className="h-5 w-5 text-amber-500" />{text}</div>)}
          </Reveal>
        </div>
      </section>

      <section id="faq" className="px-6 py-24 lg:py-32">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.75fr_1.25fr]">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.24em] text-amber-700">Questions</p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">Clear answers before you buy.</h2>
            <p className="mt-5 text-base leading-8 text-[#525866]">Still unsure? Send a note through the contact page and include the product or workflow you are asking about.</p>
          </Reveal>
          <Reveal>
            <Accordion type="single" collapsible className="rounded-[2rem] border border-[#E6E2DA] bg-white px-6 shadow-sm">
              {faqs.map(([question, answer], index) => (
                <AccordionItem key={question} value={`item-${index}`}>
                  <AccordionTrigger data-testid={`faq-question-${index + 1}`} className="text-left font-heading font-bold">{question}</AccordionTrigger>
                  <AccordionContent className="leading-7 text-[#525866]">{answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <section className="px-6 pb-24">
        <Reveal className="mx-auto max-w-7xl overflow-hidden rounded-[2.5rem] bg-[#D97706] p-10 text-white md:p-16">
          <div className="grid items-center gap-8 lg:grid-cols-[1fr_auto]">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.24em] text-white/70">Ready when the first tool is</p>
              <h2 className="mt-4 max-w-3xl font-heading text-3xl font-extrabold tracking-tight sm:text-5xl">Build a calmer, more organized business day.</h2>
            </div>
            <Button asChild size="lg" className="rounded-full bg-white px-8 text-[#111315] hover:bg-[#F4F0EA]" data-testid="bottom-shop-cta">
              <Link to="/shop">Visit the shop <ArrowRight className="h-4 w-4" /></Link>
            </Button>
          </div>
        </Reveal>
      </section>
    </Layout>
  );
}
